/** Modal de soporte: setear un password nuevo para un usuario del comité. */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { Button, Input } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { resetPasswordUsuarioEmpresa, type UsuarioRead } from "./api";

const schema = z.object({
  nuevo_password: z.string().min(8, "Mínimo 8 caracteres").max(128),
});

type Values = z.infer<typeof schema>;

const FORM_ID = "reset-password-form";

export function ResetPasswordModal({
  empresaId,
  usuario,
  onClose,
  onSuccess,
}: {
  empresaId: number;
  usuario: UsuarioRead;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { nuevo_password: "" },
  });

  const mut = useMutation({
    mutationFn: (v: Values) =>
      resetPasswordUsuarioEmpresa(empresaId, usuario.id, v.nuevo_password),
    onSuccess: () => {
      toast.success(`Password actualizado para ${usuario.nombre}`);
      onSuccess();
    },
    onError: (err) =>
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo resetear",
      ),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Reset password"
      icon={KeyRound}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            icon={KeyRound}
            disabled={mut.isPending}
          >
            {mut.isPending ? "Guardando…" : "Setear password"}
          </Button>
        </>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={handleSubmit((v) => mut.mutate(v))}
        className="space-y-4"
      >
        <p className="text-sm text-neutral-600">
          Vas a setear un password nuevo para{" "}
          <span className="font-medium">{usuario.nombre}</span> ({usuario.email}
          ). Esta acción NO requiere el password anterior — es una operación de
          soporte. Cada reset queda en el log estructurado del backend.
        </p>
        <Input
          label="Password nuevo"
          type="text"
          {...register("nuevo_password")}
          error={errors.nuevo_password?.message}
          className="font-mono"
          placeholder="mín. 8 caracteres"
          autoFocus
        />
      </form>
    </Modal>
  );
}
